import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ColDef, GetQuickFilterTextParams, ValueFormatterFunc } from 'ag-grid-community';

export const useAbsoluteTimeCallback = (
  fallbackText?: string, // Optionally define the text to show when there is no timestamp.
): ((value?: string | null) => string) => {
  const { t } = useTranslation();
  const { showTimeAndDateFromIso } = useLocaleFormatter();

  // Format timestamp to site-local time.
  const callback = useCallback(
    (value?: string | null) => {
      return value ? showTimeAndDateFromIso(value) : (fallbackText ?? t('Never'));
    },
    [t, showTimeAndDateFromIso, fallbackText],
  );

  return callback;
};

export function useAbsoluteTime(value?: string | null, fallbackText?: string): string {
  const callback = useAbsoluteTimeCallback(fallbackText);
  return useMemo(() => callback(value), [callback, value]);
}

/**
 * Returns a callback that formats a timestamp into site-local time.
 * The returned callback can be used as the valueFormatter property of a ColDef (affects display only).
 */
export function useAbsoluteTimeValueFormatter<TData extends object>(
  fallbackText?: string,
): ValueFormatterFunc<TData, string> {
  const callback = useAbsoluteTimeCallback(fallbackText);
  return useCallback<ValueFormatterFunc<TData, string>>(({ value }) => callback(value), [callback]);
}

/**
 * Returns a callback that can be used to modify the AG Grid search (quick filter) behaviour of a column.
 * The returned callback can be used as the getQuickFilterText property of a ColDef.
 */
export function useAbsoluteTimeQuickFilterText<TData extends object>(
  fallbackText?: string,
): (params: GetQuickFilterTextParams<TData, string>) => string {
  const callback = useAbsoluteTimeCallback(fallbackText);

  return useCallback(
    ({ value: rawValue }: GetQuickFilterTextParams<TData, string>): string => {
      const formattedValue = callback(rawValue);
      // If the raw value is truthy (a timestamp), join the formatted and raw value so they can both be searched.
      return rawValue ? `${formattedValue} ${rawValue}` : formattedValue;
    },
    [callback],
  );
}

/**
 * Returns the ColDef properties that are needed to:
 * - display localised time translations.
 * - preserve sorting by timestamp (correct order regardless of translation).
 * - search by timestamp and localised time.
 */
export const useAbsoluteTimePartialColDef = <TData extends object>(
  fallbackText?: string,
): Pick<ColDef<TData>, 'valueFormatter' | 'getQuickFilterText' | 'cellRenderer'> => {
  const valueFormatter = useAbsoluteTimeValueFormatter<TData>(fallbackText);
  const getQuickFilterText = useAbsoluteTimeQuickFilterText<TData>(fallbackText);

  return {
    valueFormatter, // Change display only.
    getQuickFilterText, // Joins the localised string and raw value for searching.
  };
};
